
import StatusBadge from 'common/StatusBadge';
import ButtonSecondary from 'common/ButtonSecondary';
import SubHeading from 'common/SubHeading';
import styled from 'styled-components';
import { ChevronRight } from '@mui/icons-material';

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    businessData as businessDataAtom, userDetails as userDetailsAtom
} from 'atoms';
import { useRecoilState } from 'recoil';
import { getBusinessDataForUserApi } from 'SupabseHandler';
import { toast } from 'react-toastify';
import { loadStripe } from '@stripe/stripe-js';
import Loader from 'common/Loaders/Loader';
import EmptyReceivingAccount from './EmptyReceivingAccount';
import StripeActionBanner from './StripeActionBanner';
import { useAuth } from 'context/AuthContext';

const StyledDiv = styled.div`
    display: flex;
    flex-direction: column;
    padding-top: 10px;

    .headerRow {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
    }

    .accountDetail {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 32px;
        border-radius: 12px;
        border: 1px solid ${(props) => props.theme.border};
        background: ${(props) => props.theme.colorBoxBackground};
        cursor: pointer;
        transition: background-color 0.2s ease;
        position: relative;
        flex-shrink: 0;
        gap: 16px;

        @media (max-width: 960px) {
            padding: 20px;
        }

        @media (max-width: 640px) {
            padding: 16px;
            gap: 12px;
        }

        &:hover {
            background: white;
        }

        .manageButton {
            opacity: 1;
            transition: opacity 0.2s ease;
        }
    }

    /* Prevent text shrinking on mobile */
    .sub-heading {
        font-size: 28px !important;
        flex-shrink: 0;
    }

    /* Bank info container - responsive gap */
    .accountDetail > div:first-child {
        @media (max-width: 640px) {
            gap: 12px;
        }
    }
`;

const PageHeader = ({ onAccountStatusChange }) => {
    const { token, loading: authLoading } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    
    

    const [businessData, setBusinessData] = useRecoilState(businessDataAtom);
    const [user, setUser] = useRecoilState(userDetailsAtom);
    const [accountDetail, setAccountDetail] = useState(null);
    const [accountHealth, setAccountHealth] = useState(null);
    const [receiving, setReceiving] = useState(null);
    const [loading , setLoading] = useState(true); // Start as true for initial load
    const params = new URLSearchParams(location.search);

    // Centralized helper to ensure we have a valid auth token for backend API calls
    const ensureAuthToken = () => {
        if (!token) {
            console.error('No authentication token found');
            throw new Error('No authentication token found');
        }
        return token;
    };

    // A receiving account exists once Stripe onboarding has actually been submitted.
    //
    // Do NOT infer this from `accountDetail` (the bank account). Providers are onboarded
    // as Stripe STANDARD accounts now, and Stripe only returns `external_accounts` to the
    // platform when the platform controls the account — so `accountDetail` is null for
    // every provider onboarded since the direct-charge migration, however complete their
    // setup is. That made the portal show "Set Up Receiving Account" forever and hid the
    // invoice list from providers who were already taking payments.
    //
    // The `: Boolean(accountDetail)` arm only runs against a backend that predates the
    // `receiving` summary. It reinstates the old (Standard-broken) behaviour rather than
    // telling EVERY provider they have no account — deploy the backend first and it never
    // runs.
    const hasReceivingAccount = receiving
        ? Boolean(receiving.details_submitted)
        : Boolean(accountDetail);
    // Same fallback rule: against an old backend a visible bank account meant "Active",
    // so keep saying that rather than flagging every legacy provider as "Action needed".
    const canReceivePayments = receiving
        ? Boolean(receiving.can_receive_payments)
        : Boolean(accountDetail);

    // Notify parent when account status changes
    useEffect(() => {
        if (onAccountStatusChange) {
            onAccountStatusChange(hasReceivingAccount);
        }
    }, [hasReceivingAccount, onAccountStatusChange]);


    const handleStripeOnboarding = async () => {
        try {
            // Get the current origin to construct the return URL
            const returnUrl = `${window.location.origin}${window.location.pathname}`;
            
            const authToken = ensureAuthToken();
            
            const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/v1/stripe/onboard-user`, {
                method: 'POST',
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${authToken}`,
                  },
                body: JSON.stringify({ 
                    email: user?.user?.email,
                    return_url: returnUrl
                })
            });
            const data = await response.json();
            
            if (data.error) {
                throw new Error(data.error);
            }
 
            // Save the account ID via backend API instead of direct Supabase update
            const updateResponse = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/update-business`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authToken}`,
                    },
                    body: JSON.stringify({
                        id: businessData?.id,
                        stripe_id: data?.stripe_account_id,
                        // /onboard-user now creates Standard accounts (direct charges).
                        stripe_account_type: 'standard',
                    }),
                },
            );

            const updateResult = await updateResponse.json().catch(() => ({}));

            if (!updateResponse.ok || updateResult.error) {
                throw new Error(updateResult.error || 'Failed to update business Stripe account');
            }
            
            // Refresh business data to get the updated stripe_id
            const updatedBusinessData = await getBusinessDataForUserApi(user?.user?.id, authToken);
            if (updatedBusinessData && updatedBusinessData[0]) {
                setBusinessData(updatedBusinessData[0]);
            }
            
            if (data.url) {
                // Open Stripe onboarding in a new tab instead of the current one
                window.open(data.url, '_blank', 'noopener,noreferrer');
            }
            
            return data?.stripe_account_id;
        } catch (error) {
            console.error('Error:', error);
            throw error;
        }
    };
    const fetchProviderAccount = async (stripeId = null) => {
        try {
            const accountId = stripeId || businessData?.stripe_id;
            if (!accountId) {
                console.warn("No Stripe account ID available to fetch account details");
                return;
            }

            if (authLoading) {
                return; // Wait for auth to finish loading
            }

            const authToken = ensureAuthToken();

            // Fetch full account (bank details) and health snapshot in parallel
            const [accountResponse, healthResponse] = await Promise.all([
                fetch(`${process.env.REACT_APP_SERVER_URL}/api/v1/stripe/get-account/${accountId}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${authToken}`,
                    },
                }),
                fetch(`${process.env.REACT_APP_SERVER_URL}/api/v1/stripe/account-health/${accountId}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${authToken}`,
                    },
                }),
            ]);

            const accountData = await accountResponse.json();
            if (accountData.error) {
                console.error("Error from get-account API:", accountData.error);
                return;
            }
            // `receiving` is the account-type-independent summary; `bank` is only ever
            // populated for account types whose external accounts Stripe exposes to us.
            // Fall back to the raw external_accounts read only when the backend is too
            // old to send `receiving` at all (see hasReceivingAccount).
            setReceiving(accountData?.receiving ?? null);
            setAccountDetail(
                accountData?.receiving
                    ? accountData.receiving.bank ?? null
                    : accountData?.account?.external_accounts?.data?.[0] ?? null
            );

            const healthData = await healthResponse.json();
            if (!healthData.error) {
                setAccountHealth(healthData?.health ?? null);
            }
        } catch (error) {
            console.error("Error fetching account details:", error);
        }
    };

    useEffect(() => {
        if (businessData?.stripe_id && !authLoading && token) {
            fetchProviderAccount().finally(() => {
                setLoading(false);
            });
        } else if (!businessData?.stripe_id) {
            // If no stripe_id, we're done loading
            setLoading(false);
        }
    }, [businessData?.stripe_id, authLoading, token])

    const fetchBusinessdata = async() =>{
        if (authLoading) {
            return; // Wait for auth to finish loading
        }
        try {
            const authToken = ensureAuthToken();
            const businessData = await getBusinessDataForUserApi(user?.user?.id, authToken);
            setBusinessData(businessData[0]);
        } catch (error) {
            console.error('Error fetching business data:', error);
        }
    }   
    
    useEffect(() => {
        if (!authLoading && token) {
            fetchBusinessdata();
        }
    }, [authLoading, token]);



    const checkIfStripeIdExist = async () => {
        try {
            setLoading(true);

            const authToken = ensureAuthToken();

            // Fetch the Stripe account ID from backend API instead of Supabase
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/business/${businessData?.id}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authToken}`,
                    },
                },
            );

            if (!response.ok) {
                throw new Error('Stripe Account ID not found.');
            }

            const result = await response.json();
            const rawData = result?.data ?? result ?? {};
            // Backend returns data as an array; use the first record
            const providerData = Array.isArray(rawData) ? rawData[0] : rawData;
            
            if (!providerData?.stripe_id) {
                // Handle new onboarding
                const newStripeAccountId = await handleStripeOnboarding();
                // Wait a bit for the database update to propagate, then fetch account
                if (newStripeAccountId) {
                    // Use the new account ID directly instead of waiting for state update
                    await fetchProviderAccount(newStripeAccountId);
                }
            } else {
                // Handle existing account
                await handleManageAccount();
                // Fetch account details for existing account
                await fetchProviderAccount(providerData.stripe_id);
            }

        } catch (error) {
            console.error("Error managing Stripe account:", error);
            toast.error(error?.message || "Failed to set up receiving account. Please try again.");
        } finally {
            // Ensure the loader is stopped even after opening Stripe in a new tab
            setLoading(false);
        }
    };
    const connectStandardStripeAccount = async () => {
        try {
            const authToken = ensureAuthToken();
            
          const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/v1/stripe/create-oauth-link`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${authToken}`,
              },
            body: JSON.stringify({ email: user?.user?.email,userid:user?.user?.id }), // Use logged-in user’s email
          });
      
          const data = await res.json();
          if (data.url) {
            // 👇 Redirect user to Stripe OAuth
            window.location.href = data.url;
          } else {
            alert("Failed to generate Stripe link.");
          }
        } catch (err) {
          console.error("Stripe OAuth error:", err);
        }
      };
      

    const handleManageAccount = async () => {
        try {
            const authToken = ensureAuthToken();
            
            // Call the backend to get the dashboard link
            const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/v1/stripe/manage-account`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${authToken}`,
                  },
                body: JSON.stringify({ stripeAccountId: businessData?.stripe_id }),
            });

            const data = await response.json();
            if (data.url) {
                // Open Stripe dashboard in a new tab instead of the current one
                window.open(data.url, '_blank', 'noopener,noreferrer');
            }



        } catch (error) {
            console.error("Error managing Stripe account:", error);
        }
    };

    return (
        <>
            {loading ? (
                <Loader />
            ) : !hasReceivingAccount ? (
                <EmptyReceivingAccount onSetup={checkIfStripeIdExist} isLoading={loading} />
            ) : (
                <StyledDiv>
                    <div className="headerRow" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                        <SubHeading className="sub-heading" style={{ fontSize: '28px', flexShrink: 0 }}>Receiving Account</SubHeading>
                    </div>
                    <StripeActionBanner
                        health={accountHealth}
                        onFixClick={handleManageAccount}
                    />
                    <div className="accountDetail" tabIndex={0} role="button" aria-label="Open Stripe Dashboard">
                        <div style={{ 
                            display: 'flex', 
                            gap: '16px',
                            alignItems: 'center', 
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden'
                        }}>
                            {accountDetail ? (
                                <>
                                    <span
                                        className="br"
                                        style={{
                                            textTransform: 'capitalize',
                                            fontSize: '14px',
                                            flexShrink: 0,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {accountDetail?.bank_name}
                                    </span>
                                    <span style={{ color: '#ebebeb', fontSize: '14px', flexShrink: 0 }}>|</span>
                                    <span style={{
                                        fontSize: '14px',
                                        flexShrink: 0,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}>
                                        .... .... .... {accountDetail?.last4}
                                    </span>
                                    <span style={{ color: '#ebebeb', fontSize: '14px', flexShrink: 0 }}>|</span>
                                    <StatusBadge
                                        status={canReceivePayments ? 'Active' : 'Pending'}
                                        badgeName={canReceivePayments ? 'Active' : 'Action needed'}
                                    />
                                </>
                            ) : (
                                // Standard accounts: Stripe doesn't share the provider's bank
                                // details with us, so show what we can actually vouch for —
                                // whether money can reach them — instead of a bank we can't see.
                                <>
                                    <span style={{
                                        fontSize: '14px',
                                        color: '#3E3E4A',
                                        flexShrink: 0,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}>
                                        {receiving?.email || 'Connected Stripe account'}
                                    </span>
                                    <span style={{ color: '#ebebeb', fontSize: '14px', flexShrink: 0 }}>|</span>
                                    <StatusBadge
                                        status={canReceivePayments ? 'Active' : 'Pending'}
                                        badgeName={canReceivePayments ? 'Active' : 'Action needed'}
                                    />
                                </>
                            )}
                        </div>
                        <ButtonSecondary
                            onClick={(e) => {
                                e.stopPropagation();
                                checkIfStripeIdExist();
                            }}
                            className="manageButton"
                            style={{
                                gap: '6px',
                                padding: '8px 10px 8px 16px',
                                fontSize: '12px',
                                flexShrink: 0,
                                minWidth: 'fit-content',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Open Stripe Dashboard
                            <ChevronRight
                                fontSize="small"
                                style={{
                                    fontSize: '16px',
                                    margin: 0,
                                    width: '16px',
                                    height: '16px',
                                    minWidth: '16px',
                                    minHeight: '16px',
                                    flexShrink: 0,
                                }}
                            />
                        </ButtonSecondary>
                    </div>
                </StyledDiv>
            )}
        </>
    );
};

export default PageHeader;